import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { uniq } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
  getSingleSelectColumnEnumValueOptions,
} from '~/components/SolaForceDataGrid';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { EmployeeEquipmentReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helpers/date';
import { getEnumName } from '~/utils/helpers/enum';

type EmployeeEquipmentReportRow_Employee = {
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
};

type EmployeeEquipmentReportRow_Equipment = {
  equipmentType: string;
  equipmentItem: string;
  equipmentInfo: string;
  equipmentInventoryNumber: string;
  equipmentDeliveryDate?: Date;
  equipmentReturnedDate?: Date;
  equipmentExpirationDate?: Date;
};

export type EmployeeEquipmentReportRow = {
  rowId: string;
} & EmployeeEquipmentReportRow_Employee &
  EmployeeEquipmentReportRow_Equipment;

type PropsType = {
  data: EmployeeEquipmentReportData[];
};

export const useEmployeeEquipmentReport = (props: PropsType) => {
  const { data } = props;

  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    return {
      equipmentTypeEnums: getEnums(EnumListKey.EQUIPMENT_TYPE),
    };
  }, [getEnums]);

  const { rows, equipmentTypeOptions, counts } = useMemo(() => {
    const { equipmentTypeEnums } = enums;

    const rows = data
      .map((employeeData, employeeIndex) => {
        const {
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpEmployeeNumber,
          fEmpEquipment,
        } = employeeData;

        const employee: EmployeeEquipmentReportRow_Employee = {
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          employeeNumber: fEmpEmployeeNumber,
        };

        return fEmpEquipment.map(
          (equipmentData, equipmentIndex): EmployeeEquipmentReportRow => {
            const {
              fEquipmentType,
              fEquipmentItem,
              fEquipmentInfo,
              fEquipmentInvNumber,
              fEquipmentDeliveryDate,
              fEquipmentReturnDate,
              fEquipmentExpirationDate,
            } = equipmentData;

            const equipment: EmployeeEquipmentReportRow_Equipment = {
              equipmentType: getEnumName(equipmentTypeEnums, fEquipmentType),
              equipmentItem: fEquipmentItem,
              equipmentInfo: fEquipmentInfo ?? '',
              equipmentInventoryNumber: fEquipmentInvNumber,
              equipmentDeliveryDate: convertToDateOrUndefined(
                fEquipmentDeliveryDate,
              ),
              equipmentReturnedDate:
                convertToDateOrUndefined(fEquipmentReturnDate),
              equipmentExpirationDate: convertToDateOrUndefined(
                fEquipmentExpirationDate,
              ),
            };

            return {
              rowId: `${employee.employeeId}-${employeeIndex}-${equipmentIndex}`,
              ...employee,
              ...equipment,
            };
          },
        );
      })
      .flat();

    const presentEquipmentTypeCodes = uniq(
      data
        .map((employee) => {
          const { fEmpEquipment } = employee;

          return fEmpEquipment.map((equipment) => equipment.fEquipmentType);
        })
        .flat(),
    );
    const equipmentTypeOptions = getSingleSelectColumnEnumValueOptions(
      equipmentTypeEnums,
      presentEquipmentTypeCodes,
    );

    const counts = {
      employees: data.length,
      equipment: rows.length,
    };

    return { rows, equipmentTypeOptions, counts };
  }, [data, enums]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<EmployeeEquipmentReportRow>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 60,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
    ];

    const equipmentColumnsConfigs: GridColDef<EmployeeEquipmentReportRow>[] = [
      {
        field: 'equipmentType',
        headerName: t('laType'),
        type: 'singleSelect',
        minWidth: 130,
        valueOptions: equipmentTypeOptions,
      },
      {
        field: 'equipmentItem',
        headerName: t('laEquipmentItem'),
        minWidth: 180,
      },
      {
        field: 'equipmentInventoryNumber',
        headerName: t('laEquipmentNumber'),
        minWidth: 170,
      },
      {
        field: 'equipmentDeliveryDate',
        headerName: t('laDeliveryDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        minWidth: 130,
      },
      {
        field: 'equipmentReturnedDate',
        headerName: t('laReturnedDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        minWidth: 130,
      },
      {
        field: 'equipmentInfo',
        headerName: t('laEquipmentInfo'),
        minWidth: 170,
        flex: 1,
      },
      {
        field: 'equipmentExpirationDate',
        headerName: t('laExpires'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        minWidth: 130,
      },
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'equipment',
        headerName: t('equipment'),
        children: getFieldNamesFromColumnConfig(equipmentColumnsConfigs),
      },
    ];

    return {
      columnConfigs: [...employeeColumnConfigs, ...equipmentColumnsConfigs],
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, equipmentTypeOptions]);

  return {
    table: {
      rows: rows,
      columnConfigs,
      columnGroupingModel,
    },
    counts,
  };
};
